import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import { formatCurrency, formatNumber, formatIndianNumber } from '../utils/formatters';

const { width } = Dimensions.get('window');

const PPFCalculatorScreen = ({ navigation }) => {
  const { colors, isDarkMode } = useTheme();
  
  // State variables
  const [results, setResults] = useState(null);
  const [showFAQ, setShowFAQ] = useState(true);
  const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false);

  // Screen dimensions for responsive design
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const isWeb = Platform.OS === 'web';
  const isMobile = screenData.width < 900;

  // Update screen dimensions on resize
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenData(window);
    });
    return () => subscription?.remove();
  }, []);
  
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isCalculated, setIsCalculated] = useState(false);
  
  const [formData, setFormData] = useState({
    yearlyInvestment: '150000', // Yearly PPF Investment (Max 1.5 lakh)
    interestRate: '7.1', // Current PPF Interest Rate (FY 2024-25)
    duration: '15', // Duration of investment (15 years minimum)
    frequency: 'Yearly', // PPF is typically yearly
  });

  // Calculate PPF maturity amount
  const calculatePPF = () => {
    const yearlyAmount = parseFloat(formData.yearlyInvestment) || 0;
    const annualRate = parseFloat(formData.interestRate) / 100;
    const years = parseFloat(formData.duration) || 15;
    
    // PPF minimum is 15 years
    const actualYears = Math.max(years, 15);
    
    let totalAmount = 0;
    let totalInvestment = 0;
    
    if (formData.frequency === 'Monthly') {
      const monthlyAmount = yearlyAmount / 12;
      const monthlyRate = annualRate / 12;
      const totalMonths = actualYears * 12;
      totalInvestment = monthlyAmount * totalMonths;
      
      // Future Value of Annuity formula for monthly
      if (monthlyRate > 0) {
        totalAmount = monthlyAmount * (((1 + monthlyRate) ** totalMonths - 1) / monthlyRate);
      } else {
        totalAmount = totalInvestment;
      }
    } else {
      // Yearly investment
      totalInvestment = yearlyAmount * actualYears;
      
      // Future Value of Annuity formula for yearly
      if (annualRate > 0) {
        totalAmount = yearlyAmount * (((1 + annualRate) ** actualYears - 1) / annualRate);
      } else {
        totalAmount = totalInvestment;
      }
    }
    
    const interestEarned = totalAmount - totalInvestment;
    const taxSaved = totalInvestment * 0.3; // 30% tax saving under Section 80C
    
    setResults({
      maturityAmount: totalAmount,
      totalInvestment: totalInvestment,
      interestEarned: interestEarned,
      taxSaved: taxSaved,
      actualYears: actualYears,
    });
    setIsCalculated(true);
  };

  // Auto-calculate when form data changes
  useEffect(() => {
    calculatePPF();
  }, [formData]);

  const renderHeader = () => (
    <View style={styles.header}>
      {isMobile && (
        <TouchableOpacity 
          style={styles.hamburgerButton}
          onPress={() => setSidebarVisible(!sidebarVisible)}
        >
          <Ionicons name="menu" size={24} color="white" />
        </TouchableOpacity>
      )}
      <Text style={styles.headerTitle}>PPF Calculator</Text>
      <Text style={styles.headerSubtitle}>
        Calculate your Public Provident Fund maturity amount with our comprehensive PPF calculator. 
        Plan your long-term savings with tax benefits and understand how your PPF investments grow over 15+ years.
      </Text>
      
      <View style={styles.headerTags}>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>Tax Free Returns</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>15 Year Lock-in</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>Section 80C</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>100% Free</Text>
        </View>
      </View>
    </View>
  );

  const renderYearlyInvestmentInput = () => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>Yearly PPF Investment (Max ₹1.5 Lakh)</Text>
      <View style={styles.inputContainer}>
        <Text style={styles.currencySymbol}>₹</Text>
        <TextInput
          style={styles.input}
          value={formData.yearlyInvestment}
          onChangeText={(value) => {
            // Limit to 1.5 lakh
            const numValue = parseFloat(value) || 0;
            const limitedValue = Math.min(numValue, 150000).toString();
            setFormData({...formData, yearlyInvestment: limitedValue});
          }}
          placeholder="150000"
          keyboardType="numeric"
          placeholderTextColor={colors.textSecondary}
        />
      </View>
      <Text style={styles.helperText}>Maximum annual investment limit is ₹1,50,000</Text>
    </View>
  );

  const renderInterestRateInput = () => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>Current PPF Interest Rate (FY 2024-25)</Text>
      <View style={styles.inputContainer}>
        <TextInput
          style={styles.input}
          value={formData.interestRate}
          onChangeText={(value) => setFormData({...formData, interestRate: value})}
          placeholder="7.1"
          keyboardType="numeric"
          placeholderTextColor={colors.textSecondary}
        />
        <Text style={styles.percentSymbol}>%</Text>
      </View>
    </View>
  );

  const renderDurationInput = () => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>Duration of Investment (Minimum 15 Years)</Text>
      <View style={styles.inputContainer}>
        <TextInput
          style={styles.input}
          value={formData.duration}
          onChangeText={(value) => {
            // Minimum 15 years for PPF
            const numValue = parseFloat(value) || 15;
            const limitedValue = Math.max(numValue, 15).toString();
            setFormData({...formData, duration: limitedValue});
          }}
          placeholder="15"
          keyboardType="numeric"
          placeholderTextColor={colors.textSecondary}
        />
        <Text style={styles.percentSymbol}>Yrs</Text>
      </View>
      <Text style={styles.helperText}>PPF has a mandatory lock-in period of 15 years</Text>
    </View>
  );

  const renderFrequencyDropdown = () => {
    const frequencies = ['Yearly', 'Monthly'];
    
    return (
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Frequency of Investment</Text>
        <TouchableOpacity
          style={styles.dropdownContainer}
          onPress={() => setShowFrequencyDropdown(!showFrequencyDropdown)}
        >
          <Text style={styles.dropdownText}>{formData.frequency}</Text>
          <Ionicons 
            name={showFrequencyDropdown ? "chevron-up" : "chevron-down"} 
            size={20} 
            color={colors.text} 
          />
        </TouchableOpacity>
        
        {showFrequencyDropdown && (
          <View style={styles.dropdownMenu}>
            {frequencies.map((freq) => (
              <TouchableOpacity
                key={freq}
                style={styles.dropdownItem}
                onPress={() => {
                  setFormData({...formData, frequency: freq});
                  setShowFrequencyDropdown(false);
                }}
              >
                <Text style={styles.dropdownItemText}>{freq}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>
    );
  };

  const renderCalculator = () => (
    <View style={styles.calculatorContainer}>
      <View style={styles.calculatorRow}>
        <View style={styles.formContent}>
          {renderYearlyInvestmentInput()}
          {renderInterestRateInput()}
          {renderDurationInput()}
          {renderFrequencyDropdown()}
        </View>
        
        {!isMobile && (
          <View style={styles.summaryPanel}>
            <View style={styles.panelHeader}>
              <Text style={styles.panelTitle}>PPF Maturity Amount</Text>
            </View>
            
            <View style={styles.resultCard}>
              <Text style={styles.resultLabel}>Maturity Amount</Text>
              <Text style={styles.resultValue}>
                ₹{results ? formatIndianNumber(Math.round(results.maturityAmount)) : '32,54,567.38'}
              </Text>
            </View>
            
            <View style={styles.resultCard}>
              <Text style={styles.resultLabel}>Total Investment</Text>
              <Text style={styles.resultAmount}>
                ₹{results ? formatIndianNumber(Math.round(results.totalInvestment)) : '22,50,000'}
              </Text>
            </View>
            
            <View style={styles.resultCard}>
              <Text style={styles.resultLabel}>Interest Earned</Text>
              <Text style={styles.resultAmount}>
                ₹{results ? formatIndianNumber(Math.round(results.interestEarned)) : '10,04,567.38'}
              </Text>
            </View>
            
            <View style={styles.resultCard}>
              <Text style={styles.resultLabel}>Tax Saved (80C)</Text>
              <Text style={styles.taxSavedAmount}>
                ₹{results ? formatIndianNumber(Math.round(results.taxSaved)) : '6,75,000'}
              </Text>
            </View>
            
            <View style={styles.infoBox}>
              <Text style={styles.infoText}>
                Your PPF will mature to ₹{results ? formatIndianNumber(Math.round(results.maturityAmount)) : '32,54,567.38'} 
                after {results ? results.actualYears : '15'} years. All returns are tax-free!
              </Text>
            </View>
          </View>
        )}
      </View>
    </View>
  );

  const renderResults = () => {
    if (isMobile && results) {
      return (
        <View style={styles.resultsContainer}>
          <Text style={styles.sectionTitle}>PPF Maturity Amount</Text>
          
          <View style={styles.resultCard}>
            <Text style={styles.resultLabel}>Maturity Amount</Text>
            <Text style={styles.resultValue}>
              ₹{formatIndianNumber(Math.round(results.maturityAmount))}
            </Text>
          </View>
          
          <View style={styles.resultCard}>
            <Text style={styles.resultLabel}>Total Investment</Text>
            <Text style={styles.resultAmount}>
              ₹{formatIndianNumber(Math.round(results.totalInvestment))}
            </Text>
          </View>
          
          <View style={styles.resultCard}>
            <Text style={styles.resultLabel}>Interest Earned</Text>
            <Text style={styles.resultAmount}>
              ₹{formatIndianNumber(Math.round(results.interestEarned))}
            </Text>
          </View>
          
          <View style={styles.resultCard}>
            <Text style={styles.resultLabel}>Tax Saved (80C)</Text>
            <Text style={styles.taxSavedAmount}>
              ₹{formatIndianNumber(Math.round(results.taxSaved))}
            </Text>
          </View>
          
          <View style={styles.infoBox}>
            <Text style={styles.infoText}>
              Your PPF will mature to ₹{formatIndianNumber(Math.round(results.maturityAmount))} 
              after {results.actualYears} years. All returns are tax-free!
            </Text>
          </View>
        </View>
      );
    }
    return null;
  };

  const renderPPFInformation = () => (
     <View style={styles.infoSection}>
       <Text style={styles.sectionTitle}>What is PPF Calculator?</Text>
       <Text style={styles.sectionText}>
        A Public Provident Fund (PPF) calculator is a comprehensive financial tool that helps you estimate the maturity amount, 
        interest earnings, and tax benefits of your PPF investment. PPF is India's most popular long-term 
        investment scheme backed by the Government of India with a 15-year lock-in period, offering triple tax benefits 
        under Section 80C and completely tax-free returns.
      </Text>
      
      <Text style={styles.sectionSubtitle}>What is Public Provident Fund (PPF)?</Text>
      <Text style={styles.sectionText}>
        Public Provident Fund is a government-backed savings scheme designed to encourage long-term savings among Indian citizens. 
        It combines the safety of government backing with attractive returns and comprehensive tax benefits, making it an ideal 
        choice for retirement planning and wealth creation.
      </Text>

      <Text style={styles.sectionSubtitle}>Key Benefits of PPF Investment:</Text>
      <View style={styles.featuresList}>
        <Text style={styles.featureItem}>• <Text style={styles.featureBold}>Triple Tax Benefits:</Text> Deduction under Section 80C, tax-free interest, and tax-free maturity</Text>
        <Text style={styles.featureItem}>• <Text style={styles.featureBold}>Government Guarantee:</Text> 100% safe investment backed by Government of India</Text>
        <Text style={styles.featureItem}>• <Text style={styles.featureBold}>Attractive Returns:</Text> Current interest rate of 7.1% per annum (FY 2024-25)</Text>
        <Text style={styles.featureItem}>• <Text style={styles.featureBold}>Compounding Power:</Text> Interest compounded annually for maximum growth</Text>
        <Text style={styles.featureItem}>• <Text style={styles.featureBold}>Flexible Investment:</Text> Minimum ₹500 to maximum ₹1,50,000 per year</Text>
        <Text style={styles.featureItem}>• <Text style={styles.featureBold}>Loan Facility:</Text> Available from 3rd to 6th year against PPF balance</Text>
        <Text style={styles.featureItem}>• <Text style={styles.featureBold}>Partial Withdrawal:</Text> Allowed after 7th year for emergencies</Text>
      </View>

      <Text style={styles.sectionSubtitle}>PPF Calculation Formula:</Text>
      <Text style={styles.sectionText}>
        The PPF maturity amount is calculated using the future value of annuity formula:
      </Text>
      <View style={styles.formulaBox}>
        <Text style={styles.formulaText}>M = P × [((1 + i)^n - 1) / i]</Text>
        <Text style={styles.formulaDescription}>
          Where: M = Maturity Amount, P = Annual Investment, i = Interest Rate, n = Number of Years
        </Text>
      </View>

      <Text style={styles.sectionSubtitle}>How to Use PPF Calculator:</Text>
      <View style={styles.featuresList}>
        <Text style={styles.featureItem}>1. Enter your planned yearly PPF investment (₹500 to ₹1,50,000)</Text>
        <Text style={styles.featureItem}>2. Set the current PPF interest rate (7.1% for FY 2024-25)</Text>
        <Text style={styles.featureItem}>3. Choose investment duration (minimum 15 years, extendable in 5-year blocks)</Text>
        <Text style={styles.featureItem}>4. View instant calculations of maturity amount, total investment, and interest earned</Text>
        <Text style={styles.featureItem}>5. Analyze tax savings under Section 80C for better financial planning</Text>
      </View>

      <Text style={styles.sectionSubtitle}>Types of PPF Accounts:</Text>
      <View style={styles.featuresList}>
        <Text style={styles.featureItem}>• <Text style={styles.featureBold}>Individual PPF Account:</Text> Opened by individuals for personal investment</Text>
        <Text style={styles.featureItem}>• <Text style={styles.featureBold}>Minor PPF Account:</Text> Opened by parents/guardians for children below 18 years</Text>
        <Text style={styles.featureItem}>• <Text style={styles.featureBold}>Joint PPF Account:</Text> Not allowed - only individual accounts permitted</Text>
        <Text style={styles.featureItem}>• <Text style={styles.featureBold}>HUF PPF Account:</Text> Hindu Undivided Family can open separate PPF accounts</Text>
      </View>

      <Text style={styles.sectionSubtitle}>Best Practices for PPF Investment:</Text>
      <View style={styles.featuresList}>
        <Text style={styles.featureItem}>• Invest before 5th of every month to earn interest for the entire month</Text>
        <Text style={styles.featureItem}>• Maximize annual contribution to ₹1,50,000 for optimal tax benefits</Text>
        <Text style={styles.featureItem}>• Continue investing for full 15 years to avoid account closure</Text>
        <Text style={styles.featureItem}>• Consider extending beyond 15 years in 5-year blocks for higher returns</Text>
        <Text style={styles.featureItem}>• Use partial withdrawal facility judiciously only for genuine emergencies</Text>
        <Text style={styles.featureItem}>• Plan PPF as core component of retirement portfolio</Text>
      </View>

      <View style={styles.tipBox}>
        <Text style={styles.tipTitle}>💡 Pro Tip</Text>
        <Text style={styles.tipText}>
          Start PPF investment early in your career to maximize the power of compounding. A 25-year-old investing 
          ₹1.5 lakh annually can accumulate over ₹75 lakhs by age 40 with PPF's tax-free compounding!
        </Text>
      </View>

      <View style={styles.tipBox}>
        <Text style={styles.tipTitle}>🏛️ Government Backing</Text>
        <Text style={styles.tipText}>
          PPF is backed by the Government of India, making it one of the safest investment options available. 
          Unlike market-linked investments, PPF offers guaranteed returns with zero risk to your principal amount.
        </Text>
      </View>

      <View style={styles.tipBox}>
        <Text style={styles.tipTitle}>📈 Extension Benefits</Text>
        <Text style={styles.tipText}>
          After 15 years, you can extend your PPF account in blocks of 5 years. During extension, you can either 
          continue investing or just let the existing amount grow with compound interest.
        </Text>
      </View>

      <View style={styles.tipBox}>
        <Text style={styles.tipTitle}>💰 Smart Planning</Text>
        <Text style={styles.tipText}>
          Combine PPF with other Section 80C investments like ELSS, NSC, and life insurance to create a 
          diversified tax-saving portfolio while maximizing your ₹1.5 lakh annual deduction limit.
        </Text>
      </View>
    </View>
  );

  const renderFAQSection = () => (
    <View style={styles.faqContainer}>
      <TouchableOpacity 
        style={styles.faqHeader}
        onPress={() => setShowFAQ(!showFAQ)}
      >
        <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
        <Ionicons 
          name={showFAQ ? "chevron-up" : "chevron-down"} 
          size={24} 
          color={colors.text} 
        />
      </TouchableOpacity>
      
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What is PPF and how does it work?</Text>
            <Text style={styles.faqAnswer}>
              Public Provident Fund (PPF) is a government-backed long-term savings scheme with a 15-year lock-in period. 
              It offers triple tax benefits - investment deduction under Section 80C, tax-free interest earnings, and 
              tax-free maturity amount. Interest is compounded annually and calculated monthly on the lowest balance 
              between the 5th and last day of each month.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What is the current PPF interest rate for 2024-25?</Text>
            <Text style={styles.faqAnswer}>
              The current PPF interest rate for FY 2024-25 is 7.1% per annum, compounded annually. 
              PPF interest rates are reviewed quarterly by the government and have remained stable at 7.1% since April 2020. 
              This rate is significantly higher than most fixed deposit rates and offers tax-free returns.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What is the minimum and maximum PPF investment limit?</Text>
            <Text style={styles.faqAnswer}>
              You can invest a minimum of ₹500 and maximum of ₹1,50,000 per financial year in PPF. 
              The entire investment amount qualifies for tax deduction under Section 80C. You can make deposits 
              in lump sum or multiple installments throughout the year, but ensure to invest before the 5th of 
              each month to earn interest for the full month.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Can I withdraw money from PPF before 15 years?</Text>
            <Text style={styles.faqAnswer}>
              Partial withdrawal is allowed after the 7th financial year, up to 50% of the balance at the end of 
              the 4th preceding year or the end of the preceding year, whichever is lower. 
              Complete withdrawal is only allowed after 15 years. Premature closure is not permitted, making 
              PPF a true long-term investment commitment.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What are the tax benefits of PPF investment?</Text>
            <Text style={styles.faqAnswer}>
              PPF offers triple tax benefits: (1) Investment up to ₹1.5 lakh qualifies for deduction under Section 80C, 
              (2) Interest earned annually is completely tax-free, and (3) Maturity amount after 15 years is entirely 
              tax-exempt. This makes PPF one of the most tax-efficient investment options available in India.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How is PPF interest calculated and when is it credited?</Text>
            <Text style={styles.faqAnswer}>
              PPF interest is calculated monthly on the lowest balance between the 5th and last day of each month, 
              then compounded annually. Interest is credited to your account at the end of each financial year 
              (March 31st). To maximize interest earnings, always deposit before the 5th of every month.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Can I take a loan against my PPF account?</Text>
            <Text style={styles.faqAnswer}>
              Yes, you can take a loan against your PPF balance from the 3rd to 6th financial year. The maximum 
              loan amount is 25% of the balance at the end of the 2nd preceding year or the end of the preceding 
              year, whichever is lower. The loan must be repaid within 36 months with interest at 1% above the 
              prevailing PPF rate.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What happens to PPF account after 15 years maturity?</Text>
            <Text style={styles.faqAnswer}>
              After 15 years, you have three options: (1) Withdraw the entire amount and close the account, 
              (2) Extend for another 5 years with continued investments, or (3) Extend for 5 years without 
              further investments - just let the existing amount grow. You can extend multiple times 
              in blocks of 5 years each.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Can I open multiple PPF accounts?</Text>
            <Text style={styles.faqAnswer}>
              No, an individual can hold only one PPF account in their name. However, you can open a separate 
              PPF account for your minor child. Joint PPF accounts are not allowed. If you accidentally open 
              multiple accounts, all except one will be treated as irregular and earn only savings bank interest rate.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Is PPF better than other tax-saving investments?</Text>
            <Text style={styles.faqAnswer}>
              PPF offers unique advantages: government backing ensures 100% safety, tax-free returns, and 
              attractive interest rates. While ELSS mutual funds may offer higher returns, they carry market risk. 
              Fixed deposits offer safety but taxable returns. PPF combines safety with tax efficiency, making it 
              ideal for conservative investors seeking guaranteed long-term wealth creation.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Can I transfer my PPF account from one bank to another?</Text>
            <Text style={styles.faqAnswer}>
              Yes, PPF accounts can be transferred from one bank/post office to another. You need to submit 
              a transfer application with required documents to the new bank. The transfer process typically 
              takes 2-4 weeks. There's no charge for transferring PPF accounts within the same city, but 
              nominal charges may apply for inter-city transfers.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What is the ideal PPF investment strategy?</Text>
            <Text style={styles.faqAnswer}>
              Start early to maximize compounding benefits, invest the maximum ₹1.5 lakh annually, deposit before 
              the 5th of each month for full interest, avoid premature withdrawals, and consider extending beyond 
              15 years for higher corpus. Combine PPF with other investments for a diversified portfolio 
              while utilizing the full Section 80C limit.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How accurate is the PPF calculator?</Text>
            <Text style={styles.faqAnswer}>
              PPF calculators provide highly accurate estimates based on current interest rates and investment patterns. 
              However, actual returns may vary slightly due to quarterly interest rate revisions by the government. 
              The calculator assumes consistent annual investments and current interest rates throughout the tenure 
              for projection purposes.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What documents are required to open a PPF account?</Text>
            <Text style={styles.faqAnswer}>
              Required documents include: PAN card (mandatory), Aadhaar card, passport-size photographs, 
              address proof (utility bill/bank statement), and initial deposit. For minor accounts, additional 
              documents like birth certificate and guardian's KYC documents are needed. Most banks now offer 
              online PPF account opening with digital KYC verification.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Can NRIs invest in PPF?</Text>
            <Text style={styles.faqAnswer}>
              No, Non-Resident Indians (NRIs) cannot open new PPF accounts or continue contributions to existing 
              accounts. However, if you become an NRI after opening a PPF account, the existing account can 
              continue to earn interest until maturity, but no fresh contributions are allowed. Upon returning 
              to India as a resident, you can resume contributions.
            </Text>
          </View>
        </View>
      )}
    </View>
  );

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      backgroundColor: colors.primary,
      paddingHorizontal: 20,
      paddingVertical: 40,
      paddingTop: Platform.OS === 'web' && Dimensions.get('window').width >= 900 ? 40 : 60,
    },
    hamburgerButton: {
      position: 'absolute',
      top: 20,
      left: 20,
      zIndex: 1,
      padding: 8,
    },
    headerTitle: {
      fontSize: Dimensions.get('window').width < 900 ? 24 : 32,
      fontWeight: 'bold',
      color: colors.background,
      marginBottom: 12,
      textAlign: Dimensions.get('window').width < 900 ? 'center' : 'left',
    },
    headerSubtitle: {
      fontSize: 16,
      color: colors.background + 'E6',
      lineHeight: 24,
      marginBottom: 20,
      textAlign: Dimensions.get('window').width < 900 ? 'center' : 'left',
    },
    headerTags: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      justifyContent: Dimensions.get('window').width < 900 ? 'center' : 'flex-start',
    },
    headerTag: {
      backgroundColor: colors.background + '33',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
    },
    headerTagText: {
      color: colors.background,
      fontSize: 12,
      fontWeight: '500',
    },
    calculatorContainer: {
      backgroundColor: colors.background,
      margin: 0,
      padding: 20,
    },
    formContent: {
      gap: 20,
      flex: 0.7,
    },
    calculatorRow: {
      flexDirection: 'row',
      gap: 24,
    },
    summaryPanel: {
      flex: 0.3,
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      height: 'fit-content',
      borderWidth: 1,
      borderColor: colors.border,
    },
    panelHeader: {
      marginBottom: 20,
    },
    panelTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
    },
    inputGroup: {
      marginBottom: 20,
    },
    label: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    helperText: {
      fontSize: 12,
      color: colors.textSecondary,
      marginTop: 4,
      fontStyle: 'italic',
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.cardBackground,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: 12,
      height: 48,
    },
    currencySymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      marginRight: 8,
    },
    percentSymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      marginLeft: 8,
    },
    input: {
      flex: 1,
      fontSize: 16,
      color: colors.text,
      borderWidth: 0,
    },
    dropdownContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: colors.cardBackground,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: 12,
      height: 48,
    },
    dropdownText: {
      fontSize: 16,
      color: colors.text,
    },
    dropdownMenu: {
      backgroundColor: colors.cardBackground,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
      marginTop: 4,
      elevation: 3,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
    },
    dropdownItem: {
      padding: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    dropdownItemText: {
      fontSize: 16,
      color: colors.text,
    },
    resultCard: {
      backgroundColor: colors.cardBackground,
      padding: 16,
      borderRadius: 8,
      marginBottom: 12,
      borderWidth: 1,
      borderColor: colors.border,
    },
    resultLabel: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 4,
    },
    resultValue: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.success,
    },
    resultAmount: {
      fontSize: 18,
      fontWeight: '600',
      color: colors.text,
    },
    taxSavedAmount: {
      fontSize: 18,
      fontWeight: '600',
      color: colors.error,
    },
    infoBox: {
      backgroundColor: colors.primary + '20',
      padding: 12,
      borderRadius: 8,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
    },
    infoText: {
      fontSize: 14,
      color: colors.primary,
      lineHeight: 20,
    },
    resultsContainer: {
      margin: 20,
    },
    sectionTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 16,
    },
    infoSection: {
      margin: 20,
      backgroundColor: colors.surface,
      padding: 20,
      borderRadius: 12,
      elevation: 2,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
    },
    sectionText: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
      marginBottom: 16,
    },
    sectionSubtitle: {
      fontSize: 18,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 12,
    },
    featuresList: {
      marginLeft: 8,
    },
    featureItem: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
      marginBottom: 8,
    },
    featureBold: {
      fontWeight: 'bold',
      color: colors.text,
    },
    formulaBox: {
      backgroundColor: colors.cardBackground,
      padding: 16,
      borderRadius: 8,
      marginVertical: 12,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
    },
    formulaText: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
      fontFamily: 'monospace',
    },
    formulaDescription: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    tipBox: {
      backgroundColor: colors.cardBackground,
      padding: 16,
      borderRadius: 8,
      marginVertical: 12,
      borderWidth: 1,
      borderColor: colors.border,
    },
    tipTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    tipText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    faqContainer: {
      backgroundColor: colors.surface,
      margin: 20,
      borderRadius: 12,
      overflow: 'hidden',
      elevation: 2,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
    },
    faqHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 20,
      backgroundColor: colors.background,
    },
    faqTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    faqContent: {
      padding: 20,
    },
    faqItem: {
      marginBottom: 20,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
    },
  });

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      {renderCalculator()}
      {renderResults()}
      {renderPPFInformation()}
      {renderFAQSection()}
      <Footer />
    </ScrollView>
  );
};

export default PPFCalculatorScreen;